import type { Dictionary } from './ru';

export const en: Dictionary = {
  common: {
    loading: 'Loading…',
    empty: 'Nothing here',
    save: 'Save',
    saved: 'Saved',
    cancel: 'Cancel',
    create: 'Create',
    add: 'Add',
    edit: 'Edit',
    remove: 'Remove',
    back: 'Back',
    search: 'Search',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    none: '— none —',
    actions: 'Actions',
    status: 'Status',
    name: 'Name',
    code: 'Code',
    confirm: 'Confirm',
    close: 'Close',
    refresh: 'Refresh',
    loadMore: 'Load more',
    optional: 'optional',
    language: 'Language',
    active: 'Active',
    inactive: 'Disabled',
    demo: 'demo',
    errorTitle: 'Something went wrong',
    notFound: 'Page not found',
    noAccess: 'You have no access to this section',
  },
  nav: {
    dashboard: 'Overview',
    catalog: 'Catalog',
    products: 'Products',
    categories: 'Categories',
    brands: 'Brands & manufacturers',
    attributes: 'Attributes',
    supply: 'Supply',
    suppliers: 'Suppliers',
    matchQueue: 'Matching',
    pricing: 'Pricing',
    priceApprovals: 'Price approvals',
    pricingRules: 'Markup rules',
    purchaseOrders: 'Purchase orders',
    warehouse: 'Warehouse',
    stock: 'Stock',
    inbound: 'Receiving',
    fulfillment: 'Pick & ship',
    stockDocuments: 'Transfers & counts',
    sales: 'Sales',
    returns: 'Returns',
    invoices: 'Invoices',
    accounting: 'Accounting',
    orders: 'Orders',
    customers: 'Customers',
    segments: 'Segments',
    search: 'Search',
    content: 'Pages',
    seo: 'SEO',
    assistant: 'AI assistant',
    aiDrafts: 'AI drafts',
    ai: 'AI',
    ads: 'Advertising',
    analytics: 'Analytics',
    promotions: 'Promotions',
    reviews: 'Reviews',
    experiments: 'A/B tests',
    sourcingRules: 'Sourcing rules',
    settings: 'Settings',
    staff: 'Staff & roles',
    audit: 'Audit log',
    integrations: 'Integrations',
    admin: 'Administration',
    logout: 'Sign out',
    interfaceLanguage: 'Interface language',
  },
  auth: {
    title: 'Admin sign-in',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign in',
    setupTitle: 'Set up two-factor authentication',
    setupHint:
      'Scan the QR code with an authenticator app (Google Authenticator, 1Password, Authy) and enter the 6-digit code.',
    secret: 'Key for manual entry',
    code: 'Code',
    verifyTitle: 'Verification code',
    verifyHint: 'Enter the 6-digit code from the app or a recovery code.',
    recoveryTitle: 'Recovery codes',
    recoveryHint:
      'Store these codes somewhere safe. Each code works once if your phone is unavailable. They will not be shown again.',
    recoverySaved: 'I have saved the codes',
    usedRecoveryCode: 'A recovery code was used. Fewer codes remain — generate new ones if needed.',
    reauthTitle: 'Confirm this action',
    reauthHint: 'This action requires your password and an app code again.',
  },
  dashboard: {
    title: 'Overview',
    hello: 'Hello, {{name}}',
    pendingApprovals: 'Prices awaiting approval',
    matchQueue: 'Unmatched supplier items',
    suppliers: 'Suppliers',
    open: 'Open',
  },
  products: {
    title: 'Products',
    new: 'New product',
    searchPlaceholder: 'Name, SKU, GTIN or MPN',
    noPackage: 'no package data',
    noPackageHint:
      'Weight or box size is missing: delivery is priced as 1 kg and lockers with compartments are not offered. Enter them on the variant or add "Package size (mm): 620*520*240" and "Package weight (kg): 5.4" to the description.',
    onlyNoPackage: 'Only without package data',
    variants: 'Variants',
    updated: 'Updated',
    status: { draft: 'Draft', active: 'Published', archived: 'Archived' },
    main: 'General',
    brand: 'Brand',
    manufacturer: 'Manufacturer',
    primaryCategory: 'Primary category',
    extraCategories: 'Additional categories',
    taxCategory: 'Tax category',
    shippingClass: 'Shipping class',
    shipping: { standard: 'Standard', bulky: 'Bulky', oversized: 'Oversized' },
    warranty: 'Warranty, months',
    translations: 'Texts',
    translationHint: 'Publishing requires texts in the languages of all markets (lv, lt, et).',
    productName: 'Name',
    slug: 'URL slug',
    slugHint: 'Empty — generated from the name. When changed, the old URL redirects.',
    shortDescription: 'Short description',
    description: 'Description',
    seoTitle: 'SEO title',
    seoDescription: 'SEO description',
    variantsTitle: 'Variants (SKU)',
    sku: 'SKU',
    gtin: 'GTIN (EAN)',
    mpn: 'MPN',
    weight: 'Weight, g',
    package: 'Package (used for shipping)',
    pkg: { weight: 'Weight, kg', length: 'Length, cm', width: 'Width, cm', height: 'Height, cm' },
    addVariant: 'Add variant',
    variantActive: 'On sale',
    attributesTitle: 'Specifications',
    attributesHint: 'Required specifications are configured on the category.',
    mediaTitle: 'Images',
    uploadImage: 'Upload image',
    imageHint: 'JPEG, PNG, WebP or AVIF up to 20 MB.',
    role: { main: 'Main', gallery: 'Gallery', swatch: 'Swatch' },
    publish: 'Publish',
    published: 'Product published',
    archive: 'Withdraw from sale',
    archiveConfirm:
      'The product disappears from the storefront and its page answers “gone” (410). Set a replacement product to redirect instead.',
    replacement: 'Replacement product ID',
    notReady: 'Missing before publishing:',
    problem: {
      missing_translation: 'text in {{locale}}',
      no_active_variant: 'an active variant',
      missing_price: 'a price (market {{locale}})',
      missing_manufacturer: 'a manufacturer (GPSR)',
      manufacturer_address_missing: 'the manufacturer address (GPSR)',
      missing_required_attribute: 'a required specification',
      missing_category: 'a category',
    },
    lockedFields: 'Protected from import',
    lockedHint: 'Fields edited manually are not overwritten by supplier imports.',
    conflict: 'Someone else changed this product. Reload the page and try again.',
    created: 'Product created',
  },
  categories: {
    title: 'Categories',
    new: 'New category',
    parent: 'Parent',
    root: 'Catalog root',
    position: 'Order',
    move: 'Move',
    attributes: 'Category specifications',
    required: 'required',
  },
  brands: {
    title: 'Brands',
    new: 'New brand',
    manufacturers: 'Manufacturers (GPSR)',
    newManufacturer: 'New manufacturer',
    legalName: 'Legal name',
    address: 'Address',
    line1: 'Street, building',
    city: 'City',
    postalCode: 'Postal code',
    country: 'Country (code)',
    email: 'Email',
    website: 'Website',
    euRp: 'EU responsible person',
    euRpHint: 'Required when the manufacturer is outside the EU.',
    noAddress: 'no address',
  },
  attributes: {
    title: 'Attributes',
    new: 'New attribute',
    type: 'Type',
    types: {
      text: 'Text',
      number: 'Number',
      bool: 'Yes/no',
      enum: 'List (single)',
      multi_enum: 'List (multiple)',
      dimension: 'Dimension',
    },
    unit: 'Unit',
    filterable: 'Storefront filter',
    searchable: 'Searchable',
    variantAxis: 'Variant axis',
    localized: 'Text per language',
    options: 'List values',
    optionCode: 'Value code',
    addOption: 'Add value',
    codeHint: 'Latin letters, digits and _. Cannot be changed later.',
  },
  suppliers: {
    title: 'Suppliers',
    new: 'New supplier',
    code: 'Code',
    legalName: 'Legal name',
    country: 'Country',
    currency: 'Currency',
    vatId: 'VAT ID',
    priority: 'Priority',
    leadTime: 'Lead time, days',
    staleAfter: 'Stock becomes stale after, min',
    buffer: 'Safety buffer, pcs',
    dropship: 'Dropshipping',
    manualOrders: 'Orders are handed over manually',
    manualOrdersHint:
      'No e-mail or API: after payment the purchase order waits in “Needs action”; staff hand it over and mark it as sent.',
    offers: 'Offers',
    unmatched: 'Unmatched',
    sources: 'Data sources',
    newSource: 'New source',
    sourceName: 'Source name',
    feedMode: 'Feed mode',
    feed: {
      full: 'Full (missing items are zeroed)',
      delta: 'Changes only',
    },
    contentLocale: 'Language of texts in the file',
    transport: 'How we get the file',
    transportType: { upload: 'Manual upload', url: 'Fetch from URL' },
    url: 'File URL',
    interval: 'Interval, min',
    mapping: 'Field mapping (JSON)',
    mappingHint:
      'Which file columns map to which fields: supplierSku, cost and stockQty are required. An example is prefilled.',
    guards: 'Bad file protection',
    maxZeroed: 'Max zeroed stock, %',
    maxPriceChanged: 'Max items with a big price change, %',
    priceThreshold: 'Big price change, %',
    upload: 'Upload file',
    uploaded: 'File accepted, dry-run in progress…',
    duplicate: 'This file was already uploaded — skipped.',
    fetchNow: 'Fetch now',
    fetchQueued: 'Fetch queued',
    runs: 'Import log',
    lastSuccess: 'Last success',
    lastError: 'Last error',
    invalidJson: 'Invalid mapping JSON',
    createProducts: 'Create products',
    createProductsHint:
      'Supplier SKUs, one per line (or comma-separated). Each becomes a draft product: title from the feed, brand, EAN, photos. Prices follow the pricing rules. Offers already linked to a product are skipped.',
    createProductsSkus: 'Supplier SKUs',
    createProductsSubmit: 'Create ({{count}})',
    createProductsQueued:
      'Creating {{count}} products in the background — they will appear in Products as drafts in a few minutes.',
    offersTitle: 'Supplier offers',
    cost: 'Cost',
    stock: 'Stock',
    matched: 'Matched',
    sourceStatus: { active: 'Working', degraded: 'Degraded', disabled: 'Disabled' },
  },
  imports: {
    title: 'Import',
    run: 'Run',
    started: 'Started',
    finished: 'Finished',
    job: 'Stage',
    phase: {
      staged: 'Checked — awaiting apply',
      blocked: 'Stopped by guards',
      applied: 'Applied',
      failed: 'Failed',
      running: 'Processing…',
    },
    rows: 'Rows',
    valid: 'valid',
    invalid: 'invalid',
    warnings: 'with warnings',
    changes: 'Changes',
    willCreate: 'new',
    willUpdate: 'changed',
    unchanged: 'unchanged',
    missing: 'Missing from file (stock will be zeroed)',
    zeroed: 'Stock becomes 0',
    bigPriceChanges: 'Big price changes',
    guardTripped: 'Guards stopped the import',
    guardHint:
      'The file looks suspicious. Check it. If it is correct, force the import (requires re-confirmation).',
    apply: 'Apply',
    applyForced: 'Force apply',
    applyQueued: 'Apply queued. This page refreshes automatically.',
    applied: 'Applied',
    appliedStats:
      'created {{created}}, updated {{updated}}, price changes {{priceChanges}}, zeroed {{zeroedMissing}}',
    matchedStats: 'matched by GTIN {{byGtin}}, by MPN {{byMpn}}, queued {{queued}}',
    sample: 'Sample rows',
    row: 'Row',
    action: 'Action',
    errors: 'Errors',
    data: 'Data',
    actionName: { create: 'new', update: 'change', error: 'error', unchanged: 'unchanged' },
    status: { running: 'Running', succeeded: 'Succeeded', failed: 'Failed', aborted: 'Aborted' },
  },
  match: {
    title: 'Product matching',
    hint: 'Supplier items that could not be linked automatically (by GTIN or MPN + brand).',
    offer: 'Supplier item',
    supplier: 'Supplier',
    candidates: 'Similar products',
    noCandidates: 'No similar products',
    link: 'Link',
    linkById: 'Link to variant (ID)',
    createProduct: 'Create draft product',
    reject: 'Not selling',
    score: 'similarity {{score}}%',
    resolved: 'Done',
  },
  prices: {
    title: 'Price approvals',
    hint: 'Price changes above the threshold await a decision. A rejected price is not proposed again until the calculation changes.',
    product: 'Product',
    market: 'Price list',
    old: 'Current',
    new: 'Proposed',
    delta: 'Change',
    reason: 'Reason',
    approve: 'Approve',
    reject: 'Reject',
    created: 'When',
  },
  po: {
    title: 'Purchase orders',
    hint: 'Orders to suppliers: dropship orders are created automatically after payment, stock replenishment manually.',
    number: 'Number',
    type: 'Type',
    supplier: 'Supplier',
    order: 'Order',
    status: 'Status',
    total: 'Total',
    created: 'Created',
    allStatuses: 'All statuses',
    sku: 'SKU',
    item: 'Item',
    qty: 'Qty',
    cost: 'Unit cost',
    shipTo: 'Ship to',
    pickupPoint: 'parcel locker',
    supplierRef: 'Supplier reference',
    send: 'Send to supplier',
    markSent: 'Mark as handed over',
    confirm: 'Supplier confirmed',
    carrier: 'Carrier',
    tracking: 'Tracking number',
    shipped: 'Shipped',
    cancel: 'Cancel purchase order',
    cancelReason: 'Cancelled by staff',
    sentAt: 'Sent',
    types: {
      dropship: 'Dropship',
      replenishment: 'Stock replenishment',
    },
    statuses: {
      draft: 'Draft',
      sent: 'Sent',
      confirmed: 'Confirmed',
      partially_received: 'Partially received',
      received: 'Completed',
      cancelled: 'Cancelled',
    },
  },
  wh: {
    scan: 'Scan or type SKU/barcode',
    add: 'Add',
    location: 'Warehouse',
    allLocations: 'All warehouses',
    sku: 'SKU',
    item: 'Item',
    code: 'SKU or barcode',
    qty: 'Qty',
    onHand: 'On hand',
    reserved: 'Reserved',
    damaged: 'Damaged',
    incoming: 'Incoming',
    available: 'Available',
    when: 'When',
    movementType: 'Movement',
    reason: 'Reason',
    searchCode: 'Search by SKU or barcode',
    stockTitle: 'Stock',
    stockHint: 'Stock in own warehouses. Every change is recorded in the movement log.',
    movementsTitle: 'Movement log',
    adjust: 'Adjustment',
    adjustHint:
      'Correct stock with a reason. Damaged goods stay in the warehouse but are not sold.',
    delta: 'Change',
    deltaHint: '+ add, − write off',
    markDamaged: 'Damaged (remove from sale)',
    purchaseOrder: 'Purchase order',
    lines: 'Lines',
    expectedAt: 'Expected',
    inboundTitle: 'Receiving',
    inboundHint: 'Expected deliveries for confirmed purchase orders. Scan items while unloading.',
    onlyExpected: 'Expected only',
    showAll: 'Show all',
    receiveTitle: 'Receive delivery',
    expected: 'Expected',
    scanned: 'Scanned',
    counted: 'Counted',
    completeReceipt: 'Complete receipt',
    receivedOk: 'Received with no discrepancies.',
    receivedWithDiscrepancies: 'Received. Discrepancies: {{count}} — saved in the log.',
    notInDelivery: '{{code}} is not in this delivery',
    notInOrder: '{{code}} is not in the order — take it out of the box',
    fulfillmentTitle: 'Pick & ship',
    fulfillmentHint:
      'Paid orders from own stock: pick, pack with a scanner, label and hand over to the carrier.',
    toPack: 'To pick and pack',
    nothingToPack: 'No orders to pick.',
    startPicking: 'Start picking',
    pack: 'Pack',
    packHint: 'Scan every item. The button is enabled when everything matches the order.',
    reset: 'Reset',
    packed: 'Packed',
    toShip: 'Hand over to carrier',
    createLabel: 'Label',
    printLabel: 'Print label',
    handOver: 'Shipped',
    documentsTitle: 'Transfers & stock counts',
    documentsHint: 'Move stock between warehouses and recount stock.',
    transfers: 'Transfers',
    newTransfer: 'New transfer',
    route: 'From → to',
    from: 'From',
    to: 'To',
    shipTransfer: 'Ship',
    receiveAll: 'Receive all',
    counts: 'Stock counts',
    newCount: 'Start a stock count',
    count: 'Stock count',
    countHint: 'Scan every unit. Differences are booked as adjustments on completion.',
    completeCount: 'Complete and book',
    countDone: 'Adjustments booked: {{count}}.',
    movements: {
      receipt: 'Receipt',
      sale: 'Shipment',
      reservation: 'Reservation',
      release: 'Release',
      adjustment: 'Adjustment',
      damage: 'Damage',
      transfer_out: 'Transfer out',
      transfer_in: 'Transfer in',
      return: 'Return',
    },
    inboundStatuses: {
      expected: 'Expected',
      in_transit: 'In transit',
      received: 'Received',
      cancelled: 'Cancelled',
    },
    transferStatuses: {
      draft: 'Draft',
      in_transit: 'In transit',
      received: 'Received',
      cancelled: 'Cancelled',
    },
    countStatuses: {
      draft: 'Draft',
      in_progress: 'In progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
  },
  ret: {
    title: 'Returns',
    hint: 'Customer withdrawals (14 days) and other returns: approve, receive goods, refund money.',
    number: 'Number',
    items: 'Units',
    kind: 'Type',
    withdrawal: '14-day withdrawal',
    other: 'Other',
    requestedAt: 'Requested',
    return: 'Return',
    condition: 'Condition',
    resellable: 'Resellable',
    damaged: 'Damaged',
    approve: 'Approve',
    reject: 'Decline',
    rejectReason: 'Reason for declining',
    restockTo: 'Put resellable items back to',
    receive: 'Goods received',
    refundCalc: 'Items {{items}} + shipping {{shipping}} = {{total}}',
    refundAmount: 'Refund amount, €',
    refundReason: 'Why less',
    refund: 'Refund money',
    pendingRefunds: 'Waiting for a manual refund',
    pendingRefundsHint:
      'Paid by bank transfer (or the provider did not answer): send the money to the customer and confirm.',
    confirmRefund: 'Money sent',
    statuses: {
      requested: 'New request',
      approved: 'Approved',
      rejected: 'Declined',
      in_transit: 'In transit',
      received: 'Goods received',
      refunded: 'Refunded',
      closed: 'Closed',
    },
  },
  inv: {
    title: 'Invoices',
    hint: 'Invoices are issued automatically on payment, credit notes on refunds. Numbering has no gaps; issued documents cannot be changed.',
    number: 'Number',
    type: 'Type',
    buyer: 'Buyer',
    taxMode: 'VAT',
    issuedAt: 'Issued',
    allTypes: 'All documents',
    search: 'Document or order number',
    types: {
      invoice: 'Invoice',
      credit_note: 'Credit note',
    },
    taxModes: {
      standard: 'Standard',
      oss: 'OSS',
      reverse_charge: 'Reverse charge',
      export: 'Export',
    },
  },
  ord: {
    title: 'Orders',
    hint: 'All orders with filters. Open an order to see payment, sourcing, shipments and history.',
    number: 'Number',
    order: 'Order',
    created: 'Created',
    customer: 'Customer',
    market: 'Market',
    payment: 'Payment',
    search: 'Number, email or last name',
    allMarkets: 'All markets',
    from: 'From',
    to: 'To',
    found: 'Found: {{count}}',
    items: 'Items',
    vat: 'VAT',
    sourcing: 'Sourcing',
    noSourcing: 'No decision — the order is not paid yet or everything is from own stock.',
    ownStock: 'own stock',
    manual: 'needs staff: {{reason}}',
    candidates: 'Candidates ({{count}})',
    excluded: 'excluded: {{reason}}',
    score: 'score {{score}}',
    timeline: 'History',
    delivery: 'Delivery',
    billing: 'Billing',
    customerCard: 'Customer card',
    payments: 'Payment',
    refundRow: 'Refund {{amount}} — {{status}}',
    fulfillment: 'Purchasing and shipments',
    documents: 'Documents',
    cancel: 'Cancel order',
    cancelHint: 'Reservations are released, supplier orders cancelled, paid money refunded.',
    refund: 'Refund money',
    refundHint: 'Refund without a return (compensation). Paid: {{paid}}.',
    confirmBank: 'Confirm bank transfer',
    confirmBankHint: 'The money has arrived — enter the amount received.',
    amount: 'Amount, €',
    reason: 'Reason',
    markDelivered: 'Delivered',
    kinds: {
      status: 'status',
      payment: 'payment',
      refund: 'refund',
      shipment: 'shipment',
      purchase: 'purchase',
      email: 'email',
      audit: 'action',
    },
    statuses: {
      NEW: 'New',
      PAYMENT_PENDING: 'Awaiting payment',
      PAID: 'Paid',
      RESERVED: 'Reserved',
      PROCESSING: 'Picking',
      PACKED: 'Packed',
      SHIPPED: 'Shipped',
      DELIVERED: 'Delivered',
      RETURN_REQUESTED: 'Return requested',
      RETURNED: 'Returned',
      REFUNDED: 'Refunded',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
    },
    paymentStatuses: {
      unpaid: 'Unpaid',
      pending: 'Pending',
      authorized: 'Authorized',
      paid: 'Paid',
      partially_refunded: 'Partially refunded',
      refunded: 'Refunded',
    },
  },
  cust: {
    title: 'Customers',
    hint: 'Customers and companies: orders and consents.',
    search: 'Email or name',
    name: 'Name',
    type: 'Type',
    account: 'Account',
    orders: 'Orders',
    spent: 'Spent',
    since: 'Since',
    marketingYes: 'Newsletter: yes',
    marketingNo: 'Newsletter: no',
    anonymized: 'Data erased (GDPR)',
    export: 'Export data (JSON)',
    anonymize: 'Anonymize',
    anonymizeConfirm: 'Permanently erase this customer’s personal data?',
    anonymizeYes: 'Yes, erase',
  },
  pricing: {
    title: 'Markup rules',
    hint: 'Price = (cost + expenses) × (1 + markup) + fixed add-on → payment fee → minimum margin floor → market VAT → rounding. A “like the supplier shop” rule takes the supplier retail price (from its product page) as the net price instead. The most specific rule wins (product > brand > supplier > category > market > global).',
    new: 'New rule',
    recalc: 'Recalculate prices',
    recalcQueued: 'Price recalculation started — it takes a few minutes.',
    rules: 'Rules',
    noRules: 'No rules — prices are not calculated. Create at least one global rule.',
    enable: 'Enable',
    disable: 'Disable',
    settings: 'General settings',
    settingsHint:
      'Apply to every price. Saving asks for your password and code and recalculates all prices.',
    saved: 'Saved, recalculation started.',
    paymentFee: 'Payment fee, %',
    operatingCost: 'Cost per order, €',
    handlingCost: 'Warehouse handling, €',
    defaultMinMargin: 'Default min. margin, %',
    changeThreshold: 'Approve price changes above, %',
    name: 'Name',
    scope: 'Scope',
    scopeId: 'ID',
    scopeIdHint: 'Market or product ID',
    markup: 'Markup, %',
    fixed: 'Add-on, €',
    minMargin: 'Min. margin, %',
    minMarginHint: 'Empty — from general settings',
    minMarginShort: 'margin ≥',
    priority: 'Priority',
    rounding: 'Rounding',
    scopes: {
      global: 'Global',
      market: 'Market',
      category: 'Category',
      brand: 'Brand',
      product: 'Product',
      supplier: 'Supplier',
    },
    basis: 'Price basis',
    bases: {
      cost: 'Cost + markup',
      supplier_retail: 'Like the supplier shop',
    },
    basesHint: {
      cost: 'Price from the supplier cost.',
      supplier_retail:
        'Price as in the supplier own shop (read daily from the product page; where the price is on the page — the source mapping, field page). No markup; the margin floor still applies.',
    },
    roundings: {
      none: 'No rounding',
      'x.99': 'To .99',
      'x.95': 'To .95',
      'x.90': 'To .90',
      integer: 'Whole euros',
    },
  },
  src: {
    title: 'Sourcing rules',
    hint: 'Own stock always first. Suppliers are then scored by weights; the most specific rule applies (product > brand > category > market > global).',
    new: 'New rule',
    defaults: 'Default weights',
    noSplit: 'no split',
    name: 'Name',
    scope: 'Applies to',
    scopeId: 'Object ID',
    scopeIdHint: 'Market, category, brand or product ID',
    weightsTitle: 'Weights (empty — default)',
    maxLead: 'Max lead time, days',
    minMargin: 'Min margin, %',
    allowSplit: 'A line may be split between suppliers',
    scopes: {
      global: 'Everywhere',
      market: 'Market',
      category: 'Category',
      brand: 'Brand',
      product: 'Product',
    },
    weights: {
      availability: 'Availability',
      time: 'Time',
      cost: 'Cost',
      shipping: 'Shipping',
      priority: 'Priority',
      margin: 'Margin',
      reliability: 'Reliability',
    },
  },
  set: {
    requireManufacturer: 'Publishing requires a manufacturer with an address (GPSR)',
    requireManufacturerHint:
      'EU Regulation 2023/988, art. 19. When off, a product can be published without a manufacturer or its address (owner decision on legal advice).',
    title: 'Settings',
    hint: 'Invoice details, returns, shipping and payment.',
    store: 'Store',
    sellerName: 'Seller name',
    regNo: 'Reg. No.',
    vatId: 'VAT ID',
    bank: 'Bank account (IBAN)',
    sellerPhone: 'Phone for customers',
    sellerPhoneHint: 'Shown in the site header as a tap-to-call link. Format: +371 26283575',
    sellerEmail: 'E-mail for customers and invoices',
    address: 'Address',
    addressHint: 'One line per row',
    saveSeller: 'Save details',
    returnsAddress: 'Returns address (in the customer email)',
    holdDays: 'Days to wait for a transfer',
    oss: 'VAT for other EU countries (OSS)',
    ossHint:
      "Without OSS registration, sales to consumers in LT/EE carry Latvian VAT while the yearly total stays under €10,000 excl. VAT. Above it the site charges the buyer's country VAT — then register for OSS.",
    ossRegistered: 'The company is registered for OSS',
    ossOther: 'Same sales through other channels this year, € excl. VAT',
    ossOtherHint: 'E.g. wisent.lv: sales to consumers in other EU countries.',
    ossPrevious: 'All such sales last year, € excl. VAT',
    ossStatus: '{{year}}: shop {{shop}}, total {{total}} of {{threshold}}.',
    ossOrigin: 'Latvian VAT',
    ossDestination: 'Buyer country VAT',
    ossOver:
      "The €10,000 threshold is exceeded: the site charges the buyer's country VAT. Register the company for OSS (VID) and tick the box.",
    blocked: 'Do not accept orders from',
    blockedHint:
      'One per line: an address (buyer@example.com) or a domain (@example.com). The buyer sees “We cannot accept this order”, without a reason.',
    shipping: 'Shipping',
    shippingPricing: 'Delivery price for customers',
    shippingPricingHint:
      'For carrier tariffs without VAT: tariff + carrier surcharges (percent of the tariff) + shop markup + VAT. Update them from the carrier invoice or website (DPD: dpd.com/lv → Piemaksas; volume: up to 50 parcels a month +15%).',
    markup: 'Shop markup per parcel, € excl. VAT',
    courierMin: 'Courier — minimum price, € incl. VAT',
    courierMinHint: 'If the courier tariff is lower, the buyer pays this minimum. 0 — no minimum.',
    carrierCol: 'Carrier',
    surcharge: {
      fuelSurchargePct: 'Fuel / energy, %',
      labourSurchargePct: 'Labour, %',
      otherSurchargePct: 'Volume and other, %',
    },
    carrier: {
      venipak: 'Venipak',
      omniva: 'Omniva',
      dpd: 'DPD',
      latvijas_pasts: 'Latvijas Pasts',
    },
    tariffNet: 'Prices are the carrier tariff excl. VAT.',
    shippingHint:
      'Prices, free shipping threshold, delivery time. Real prices come with carrier contracts.',
    byWeight: 'by weight ({{count}} steps)',
    freeFrom: 'free from {{amount}}',
    days: 'days',
    tariff: 'Tariff',
    tariffHint: 'One price (e.g. 3.99) or lines "up to grams: price" (e.g. 5000: 3.99)',
    freeFromLabel: 'Free from, € (empty — never)',
    daysMin: 'Days from',
    daysMax: 'Days to',
    payments: 'Payment methods',
    paymentsHint: 'Which methods are shown per market (the provider needs its keys).',
  },
  staff: {
    title: 'Staff & roles',
    hint: 'Admin access. Changes need a password and code confirmation.',
    people: 'Staff',
    add: 'Add staff member',
    roles: 'Roles',
    no2fa: '2FA not set up',
    disable: 'Disable',
    enable: 'Enable',
    name: 'Name',
    created:
      'Staff member created. Temporary password (hand over in person; 2FA is set up at first sign-in):',
    rolesTitle: 'Roles & permissions',
    rolesHint: 'Click a role to change its permissions. The owner always has all permissions.',
    allPermissions: 'all permissions',
    permissionsCount: 'permissions: {{count}}',
  },
  audit: {
    title: 'Audit log',
    hint: 'Every data change by staff and the system. Entries cannot be changed or deleted.',
    entityType: 'Entity type (order, product…)',
    entityId: 'Entity ID',
    more: 'Show more',
  },
  cmdk: {
    open: 'Search',
    title: 'Search',
    placeholder: 'Order number, SKU, product or email',
    nothing: 'Nothing found',
  },
  queues: {
    title: 'Needs attention',
    allClear: 'All clear — nothing is waiting.',
    manualSourcing: 'No supplier (choose manually)',
    stuckPaid: 'Paid but idle for over a day',
    stuckWarehouse: 'Not shipped for over 2 days',
    returns: 'Returns awaiting a decision',
    refunds: 'Refund manually',
    draftPos: 'Purchase orders not sent',
    lowStock: 'Running low',
    integrations: 'Integration failures',
  },
  intg: {
    title: 'Integrations',
    hint: 'External systems receive store events (orders, documents, returns). Failures are retried on a schedule; after a series of failures the integration pauses and an alert is sent.',
    add: 'Connect a system',
    empty: 'Nothing connected yet. Accounting gets exports in the Accounting section.',
    lastSuccess: 'Last successful exchange',
    deliveries: 'Queue',
    retryAll: 'Retry failed',
    retry: 'Retry',
    enable: 'Enable',
    disable: 'Disable',
    adapter: 'System',
    name: 'Name',
    keepSecret: 'Leave empty to keep the key',
    events: 'Events to send',
    statuses: {
      active: 'Working',
      degraded: 'Failing',
      disabled: 'Disabled',
    },
    deliveryStatuses: {
      pending: 'Pending',
      succeeded: 'Delivered',
      failed: 'Retrying',
      dead: 'Not delivered',
    },
    eventNames: {
      orders_status_changed: 'Orders: status changes',
      billing_document_issued: 'Invoices and credit notes',
      payments_refund_succeeded: 'Refunds',
      returns_status_changed: 'Returns',
    },
  },
  acc: {
    title: 'Accounting',
    hint: 'Exports for the accountant and the OSS report. Invoices and credit notes are issued automatically.',
    exports: 'Exports for a period',
    exportsHint: 'CSV (";" separated, opens in Excel), Excel or XML. Credit notes are negative.',
    datasets: {
      documents: 'Invoices and credit notes',
      document_lines: 'Document lines',
      payments: 'Payments received',
      refunds: 'Refunds',
    },
    oss: 'OSS report (quarter)',
    ossHint: 'B2C sales to other EU countries at the destination rates — for the VID return.',
    year: 'Year',
    quarter: 'Quarter',
    ossEmpty: 'No OSS sales in this quarter.',
    country: 'Country',
    rate: 'Rate',
    base: 'Taxable amount',
  },
  ai: {
    title: 'AI',
    hint: 'AI modules answer only from store data. When the budget is used up, AI switches off and the store keeps working.',
    budget: 'Budget',
    budgetHint:
      'Cost of every model call is recorded. Limits apply to the calendar month and day (UTC).',
    month: 'This month',
    today: 'today',
    conversations: '{{count}} conversations',
    settings: 'Modules and limits',
    settingsHint:
      'A module works only when it is switched on and an API key is configured on the server.',
    modules: {
      storefront: 'Shopping assistant (storefront)',
      admin: 'Admin assistant',
      content: 'Content drafts',
    },
    monthlyBudgetEur: 'Monthly budget, €',
    dailyBudgetEur: 'Daily budget, €',
    conversationBudgetEur: 'One customer chat, €',
    assistantModel: 'Assistant model',
    contentModel: 'Content model',
    glossary: 'Glossary for AI texts',
    glossaryHint: 'Brand and product terms to keep as written, e.g. "Blanco", "SILGRANIT".',
  },
  exp: {
    title: 'A/B tests',
    hint: 'Each visitor who allowed analytics always sees the same variant; others see the control. Result: share of visitors who paid for an order after the assignment.',
    new: 'New test',
    empty: 'No tests yet.',
    code: 'Code (used by the storefront, e.g. add_to_cart_text)',
    name: 'Name',
    hypothesis: 'Hypothesis',
    variants: 'Variants',
    variantsHint:
      'One per line: code weight config-JSON. The first is "control"; weights add up to 1. Example: b 0.5 {"buttonText":{"en":"Buy now"}}',
    badJson: 'Variant config is not valid JSON.',
    running: 'running',
    stopped: 'stopped',
    draft: 'draft',
    start: 'Start',
    stop: 'Stop',
    since: 'since',
    variant: 'Variant',
    visitors: 'Visitors',
    orders: 'Paid orders',
    conversion: 'Conversion (95 % interval)',
    uplift: 'vs control',
    srm: 'Sample ratio mismatch: visitors are not split as configured. Do not trust these results — check the assignment.',
    howToRead:
      'A difference counts when p < 0.05 (✓) and the test ran long enough (at least one full week). Do not stop a test the moment it looks good.',
  },
  rev: {
    title: 'Reviews',
    hint: 'Only customers who received the product can leave a review. Publish honest reviews, including negative ones; reject only personal data, insults or reviews not about the product.',
    status: { pending: 'To check', published: 'Published', rejected: 'Rejected' },
    empty: 'Nothing here.',
    publish: 'Publish',
    reject: 'Reject',
  },
  promo: {
    title: 'Promotions & coupons',
    hint: 'Without codes a promotion applies by itself; with codes — only when the customer enters one. Discounts are taken from the current price; VAT is calculated after the discount.',
    new: 'New promotion',
    empty: 'No promotions yet.',
    name: 'Name',
    type: 'Discount',
    types: {
      percent: 'Percent',
      fixed: 'Fixed amount',
      free_shipping: 'Free delivery',
      tiered: 'Tiers by cart amount',
    },
    percent: 'Percent',
    amount: 'Amount, €',
    tiers: 'Tiers',
    tiersHint: 'One per line: from amount € : percent, e.g. 200:10',
    minSubtotal: 'Minimum cart amount, €',
    markets: 'Countries (empty — all)',
    validFrom: 'From',
    validTo: 'To',
    usageLimit: 'Total uses limit',
    codes: 'Coupon codes',
    codesHint: 'One per line; empty — automatic promotion.',
    perCustomer: 'Uses per customer',
    stackable: 'Combines with other promotions',
    active: 'Active',
    inactive: 'Off',
    automatic: 'automatic',
    used: 'used {{count}} times',
    spent: 'discounts {{amount}}',
  },
  an: {
    title: 'Analytics',
    hint: 'Sales, margin and refunds come from all orders. Visits and the funnel count only visitors who allowed analytics cookies.',
    market: 'Country',
    allMarkets: 'All countries',
    revenue: 'Revenue',
    withVat: 'incl. VAT, before refunds',
    orders: 'Orders',
    aov: 'Average order',
    margin: 'Margin',
    costCoverage: 'cost known for {{pct}} of lines',
    refunds: 'Refunds',
    sessions: 'Sessions',
    consentOnly: 'with analytics consent',
    conversion: 'Conversion',
    customers: 'Customers',
    newCustomers: 'new: {{count}}',
    productViews: 'Product views',
    searches: 'Searches',
    funnel: 'Funnel',
    funnelHint: 'Sessions that reached each step.',
    steps: {
      session: 'Visit',
      product_view: 'Viewed a product',
      add_to_cart: 'Added to cart',
      begin_checkout: 'Started checkout',
      purchase: 'Placed an order',
    },
    cohorts: 'Customer value (LTV) by month of first purchase',
    cohortsHint:
      'Revenue per customer after refunds, cumulative: month 0 is the month of the first purchase.',
    cohort: 'First purchase',
    month: 'M{{n}}',
    products: 'Products',
    productsHint: 'From nightly aggregates: today is added the next night.',
    product: 'Product',
    views: 'Views',
    carts: 'To cart',
    purchases: 'Orders',
    viewToPurchase: 'View → order',
    netRevenue: 'Revenue excl. VAT',
    noProducts: 'No data for the period yet.',
  },
  drafts: {
    title: 'AI drafts',
    hint: 'Texts written by AI from product data. Nothing is published until you approve it; you can edit before approving.',
    bulk: 'Fill the gaps',
    bulkHint:
      'Drafts for products where the field is empty in the language. Runs in the background and stops when the AI budget is used up.',
    field: 'Field',
    language: 'Language',
    from: 'Translate from',
    limit: 'Products',
    fillGaps: 'Create drafts',
    queued: 'Queued. Drafts will appear below in a few minutes.',
    current: 'Now',
    proposed: 'Proposed',
    approve: 'Approve',
    reject: 'Reject',
    openProduct: 'Open product',
    empty: 'Nothing here.',
    generate: 'Draft with AI',
    translate: 'Translate with AI',
    created: '{{count}} drafts created — review',
    skipped: '{{count}} skipped (numbers not in the data or wrong format).',
    status: { pending: 'To review', approved: 'Approved', rejected: 'Rejected' },
    fields: {
      shortDescription: 'Short description',
      description: 'Description',
      seoTitle: 'SEO title',
      seoDescription: 'SEO description',
      faq: 'FAQ',
      translation: 'Translation',
    },
  },
  asst: {
    title: 'AI assistant',
    hint: 'Ask about sales, margin, stock and suppliers. Answers use ready-made reports only and always show the period and the data source.',
    off: 'The admin assistant is switched off. The owner can enable it in the AI section.',
    noKey: 'No AI model is configured on the server yet.',
    question: 'Your question',
    ask: 'Ask',
    thinking: 'Working on it…',
    data: 'Data',
    rows: '{{count}} rows',
    unverified: 'Check these numbers — they are not in the data: {{numbers}}',
    examples: {
      topSales: 'Top 5 products for the last 30 days',
      margin: 'Margin by supplier for the last 30 days',
      runsOut: 'What will run out of stock in 2 weeks?',
      reorder: 'What should I reorder?',
      prices: 'Which supplier prices went up?',
    },
  },
  seo: {
    title: 'SEO',
    hint: 'What search engines are missing, and redirects from old addresses. Sitemaps, canonical and structured data are built automatically.',
    coverage: 'Coverage by language',
    coverageHint: 'Active products and categories with empty texts. Red — needs work.',
    fillWithAi: 'Fill with AI drafts →',
    language: 'Language',
    products: 'Products',
    noTranslation: 'No translation',
    noSeoTitle: 'No SEO title',
    noSeoDescription: 'No SEO description',
    noDescription: 'No description',
    noAlt: 'Images without alt',
    categoriesNoText: 'Categories without text',
    worst: 'Products with the most gaps',
    languagesMissing: 'gaps in {{count}} languages',
    redirects: 'Redirects',
    redirectsHint:
      'Old addresses (e.g. from a previous site) → new ones. Product address changes are redirected automatically. Paths are without the site prefix: /old-page, not /lv-lv/old-page.',
    oldPath: 'Old address',
    newPath: 'New address',
    type: 'Type',
    moved: '301 — moved',
    gone: '410 — removed for good',
    search: 'Find an address',
  },
  cms: {
    title: 'Pages',
    hint: 'Texts of storefront pages: delivery, returns, legal. The shopping assistant also answers from them.',
    formatHint: 'Plain text: an empty line starts a paragraph, "## " a heading, "- " a list item.',
    market: 'Country',
    allMarkets: 'All countries',
    language: 'Language',
    published: 'Published',
    pageTitle: 'Title',
    body: 'Text',
    seoTitle: 'SEO title',
    seoDescription: 'SEO description',
    codes: {
      about: 'About us',
      contact: 'Contact',
      shipping: 'Delivery',
      returns: 'Returns',
      payment: 'Payment',
      warranty: 'Warranty',
      privacy: 'Privacy policy',
      terms: 'Terms of sale',
      imprint: 'Company details',
      withdrawal: 'Right of withdrawal',
      cookies: 'Cookie policy',
    },
  },
  srch: {
    title: 'Search',
    hint: 'Synonyms help customers find products by their own words. The report shows what they searched for and did not find.',
    synonyms: 'Synonyms',
    synonymsHint:
      'Words that mean the same in a language. One-way: the first word also finds the others, but not the other way round.',
    language: 'Language',
    terms: 'Words, comma-separated',
    termsPlaceholder: 'sink, washbasin',
    oneWay: 'one-way',
    add: 'Add',
    queries: 'What customers search for',
    queriesHint: 'Queries without results are a signal: add a synonym or the missing products.',
    period: 'Period',
    days: '{{count}} days',
    searches: 'Searches',
    zeroRate: 'Without results',
    zeroResults: 'Found nothing',
    noZero: 'Every search found something.',
    top: 'Popular queries',
    query: 'Query',
    results: 'Avg. results',
    clicks: 'Clicks',
    makeSynonym: 'Synonym…',
  },
  seg: {
    title: 'Segments',
    hint: 'Customer groups for newsletters and analytics. Rule-based segments are recomputed every night (and right after saving).',
    new: 'New segment',
    dynamic: 'rule-based',
    manual: 'manual',
    members: 'customers: {{count}}',
    refresh: 'Recompute',
    name: 'Name',
    code: 'Code',
    byRules: 'Members by rules',
    manualHint: 'Members of a manual segment are set from customer cards.',
    minOrders: 'At least N orders',
    maxOrders: 'At most N orders',
    orderedWithin: 'Ordered within N days',
    notOrderedFor: 'No order for N days',
    consent: 'Newsletter consent',
    any: 'Any',
    preview: 'How many customers',
    rfm: {
      champions: 'Champions',
      loyal: 'Loyal',
      new: 'New',
      at_risk: 'At risk',
      lost: 'Lost',
      regular: 'Regular',
    },
    metrics: 'Purchases',
    ltv: 'Revenue (LTV)',
    aov: 'Average order',
    lastOrder: 'Last order',
    revoke: 'Unsubscribe from newsletter',
  },
  ads: {
    title: 'Advertising',
    hint: 'Ad account spend and what it brought. The order source is the ad tags of the first visit (utm, gclid, fbclid).',
    import: 'Upload spend',
    importHint:
      'CSV from Google Ads / Meta: date, campaign, cost columns (optionally clicks, impressions, channel, currency). Re-uploading a day overwrites it.',
    channel: 'Channel',
    file: 'CSV file',
    imported: 'Rows imported: {{count}}.',
    skipped: 'Skipped lines: {{lines}}.',
    performance: 'Channel performance',
    performanceHint: 'CAC — spend per new customer, ROAS — revenue (net of refunds) per €1 spent.',
    spend: 'Spend',
    orders: 'Orders',
    revenue: 'Revenue',
    newCustomers: 'New customers',
  },
};
